import React, { ReactNode } from "react";
import { Bell, FileText } from "lucide-react";
import { UserAvatar } from "@/components/user/UserAvatar";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

interface PageHeaderProps {
  title: ReactNode;
  userName?: string;
  subtitle?: string;
  showActions?: boolean;
  avatar?: string; // Deprecated: use UserAvatar component instead
  /** Alt text for avatar image when using logo (e.g. "Logo OfCourse"). Defaults to "User avatar". */
  avatarAlt?: string;
  showAvatar?: boolean; // New: show user avatar automatically
  icon?: ReactNode;
  progressInfo?: {
    label: string;
    value: string | number;
  };
  children?: ReactNode;
}

export const PageHeader: React.FC<PageHeaderProps> = ({
  title,
  userName,
  subtitle,
  showActions = true,
  avatar, // Deprecated but kept for backward compatibility
  avatarAlt = "User avatar",
  showAvatar = true, // Show avatar by default
  icon,
  progressInfo,
  children,
}) => {
  // Use UserAvatar component if showAvatar is true, otherwise use legacy avatar prop
  const shouldShowAvatar = showAvatar || avatar;

  const actionButtonClassName = cn(
    "size-9 !p-0 shrink-0 rounded-lg border-gray-200 bg-white text-gray-500 shadow-none",
    "hover:bg-gray-50 hover:text-gray-900 hover:border-gray-300",
    "focus-visible:ring-2 focus-visible:ring-gray-900 focus-visible:ring-offset-2",
  );

  return (
    <div className="flex flex-col w-full mb-8">
      {/* Variante 3 – Sidebar Identity: accent bar + avatar médio + kicker/título/sub + ações bordered */}
      <div className="flex flex-wrap items-start gap-x-4 gap-y-3 pb-6 border-b border-gray-200">
        {/* Accent bar */}
        <div
          className="w-[3px] h-[4.5rem] bg-gray-900 rounded-full flex-shrink-0 mt-1"
          aria-hidden="true"
        />

        {/* Avatar */}
        {shouldShowAvatar && (
          <div className="flex-shrink-0">
            {avatar ? (
              <div className="w-16 h-16 rounded-full overflow-hidden">
                <img
                  src={avatar}
                  alt={avatarAlt}
                  className="w-full h-full object-cover"
                />
              </div>
            ) : (
              <UserAvatar size="lg" />
            )}
          </div>
        )}

        {/* Text block — full width under chrome on small screens so long titles wrap */}
        <div className="order-last w-full min-w-0 basis-full sm:order-none sm:flex-1 sm:basis-0 sm:w-auto">
          {icon && <div className="text-gray-500 mb-1">{icon}</div>}
          <div>
            {userName ? (
              <h1 className="text-[1.75rem] font-bold leading-[1.15] text-gray-900">
                {title}, <span>{userName}</span>!
              </h1>
            ) : (
              title
            )}
            {subtitle && (
              <p className="text-[0.9375rem] text-gray-500 mt-1 leading-relaxed">
                {subtitle}
              </p>
            )}
          </div>
          {progressInfo && (
            <div className="mt-2 hidden sm:block">
              <span className="text-sm text-gray-500">{progressInfo.label}: </span>
              <span className="text-sm font-semibold text-gray-900">{progressInfo.value}</span>
            </div>
          )}
        </div>

        {/* Actions */}
        {showActions && (
          <div className="ml-auto flex items-center gap-2 flex-shrink-0 self-start mt-1 sm:ml-0">
            <Button
              type="button"
              variant="outline"
              size="icon"
              className={actionButtonClassName}
              aria-label="Notificações"
            >
              <Bell className="size-[1.125rem] shrink-0" strokeWidth={1.75} aria-hidden="true" />
            </Button>
            <Button
              type="button"
              variant="outline"
              size="icon"
              className={actionButtonClassName}
              aria-label="Anotações"
            >
              <FileText className="size-[1.125rem] shrink-0" strokeWidth={1.75} aria-hidden="true" />
            </Button>
          </div>
        )}
      </div>

      {children && <div className="mt-4">{children}</div>}
    </div>
  );
};
